import React, { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useLocation } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { t } from "../../i18n";
import { getServiceBySlug, getLocationBySlug, generateCrossLinks } from "../../utils";
import { generatePageTitle, generateMetaDescription, generateH1, generateSchemaOrg, generateLongTailKeywords } from "../../utils/keywordSeo";
import Hero from "../../components/Hero/Hero";
import TrustBadges from "../../components/TrustBadges/TrustBadges";
import ServiceDescription from "../../components/ServiceDescription/ServiceDescription";
import TreatmentGrid from "../../components/TreatmentGrid/TreatmentGrid";
import FAQAccordion from "../../components/FAQAccordion/FAQAccordion";
import LocationMap from "../../components/LocationMap/LocationMap";
import CTABanner from "../../components/CTABanner/CTABanner";
import CrossLinks from "../../components/CrossLinks/CrossLinks";
import HreflangTags from "../../components/HreflangTags/HreflangTags";
import { extractLocaleFromPath } from "../../i18n";
import { buildServicePageSchema } from "../../utils/schemaMarkup";
import "./ServicePage.css";

export default function ServicePage() {
  const { state } = useContext(ApplicationContext);
  const { locationSlug, serviceSlug } = useParams();
  const location = useLocation();
  const { locale: detectedLocale, pathWithoutLocale } = extractLocaleFromPath(location.pathname);
  const locale = detectedLocale || state.locale || 'en';

  const service = getServiceBySlug(state.taxonomy, serviceSlug);
  const loc = getLocationBySlug(state.taxonomy, locationSlug);
  const crossLinks = generateCrossLinks(state.taxonomy, serviceSlug, locationSlug);
  const brand = state.brand || state.client;

  if (!service || !loc) {
    return (
      <div className="container section-padding">
        <h1>Page Not Found</h1>
        <p>The service or location you're looking for doesn't exist.</p>
      </div>
    );
  }

  // Keyword-driven SEO metadata
  const pageTitle = generatePageTitle(serviceSlug, loc.city, loc.state, brand.name, locale);
  const metaDescription = generateMetaDescription(serviceSlug, loc.city, loc.state, brand.name, brand.phone || state.client.phone, locale);
  const h1Text = generateH1(serviceSlug, loc.city, loc.state, locale);
  const schemaData = generateSchemaOrg(service, loc, brand);
  const longTailKeywords = generateLongTailKeywords(loc.city, loc.state, serviceSlug);
  const canonicalUrl = `https://${brand.domain || state.client.domain}/${locationSlug}/${serviceSlug}`;
  const baseDomain = brand.domain || state.client?.domain || 'gaspineortho.com';
  const servicePageSchema = buildServicePageSchema({ brand, service, location: loc });

  return (
    <div className="service-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={longTailKeywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {locale !== 'en' && <html lang={locale} />}
      </Helmet>

      {/* hreflang for all 6 language variants */}
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/${locationSlug}/${serviceSlug}`} />

      {/* Schema.org JSON-LD from keyword data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(servicePageSchema) }}
      />

      <Hero
        service={service}
        location={loc}
        client={brand}
        locale={locale}
        h1Override={h1Text}
      />

      <TrustBadges locale={locale} />

      <ServiceDescription
        service={service}
        location={loc}
        locale={locale}
        brandKey={brand?.brandKey || ''}
      />

      <TreatmentGrid
        service={service}
        locale={locale}
      />

      <FAQAccordion
        service={service}
        location={loc}
        locale={locale}
      />

      <LocationMap
        location={loc}
        client={state.client}
        locale={locale}
      />

      <CTABanner
        client={state.client}
        locale={locale}
      />

      <CrossLinks
        crossLinks={crossLinks}
        service={service}
        location={loc}
        locale={locale}
      />
    </div>
  );
}
